import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import {
  SvgIcon,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Input,
} from '@material-ui/core'
import { mdiContentSave, mdiPencil } from '@mdi/js'

import ComparatorSelect from '../controls/ComparatorSelect'
import TimeseriesSelect from '../controls/TimeseriesSelect'
import AlertTypeSelectContainer from '../controls/AlertTypeSelectContainer'

class AlertRulesListView extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    timeseries: PropTypes.array,
    alertRules: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
  }

  static defaultProps = {
    alertRules: { new: AlertRulesListView.newRule },
    timeseries: [],
  }

  static styles = () => ({
    input: {
      width: '50px',
    },
  })

  static newRule = {
    id: 'new',
    timeseries_id: 0,
    type: '',
    comparator: '',
    data: { value: 0 },
    enabled: false,
    internal: false,
  }

  state = {
    isEditing: { new: true },
    alertRules: { ...this.props.alertRules, new: AlertRulesListView.newRule },
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ alertRules: { ...nextProps.alertRules, new: AlertRulesListView.newRule } })
  }

  onChanges(id, changes) {
    this.setState(
      (prevState) => ({
        alertRules: {
          ...prevState.alertRules,
          [id]: {
            ...prevState.alertRules[id],
            ...changes,
          },
        },
      }),
    )
  }

  onChange(id, field, value) {
    this.onChanges(id, { [field]: value })
  }

  onCheckboxChange(id, e) {
    const { name, checked } = e.currentTarget
    this.onChange(id, name, checked)
  }

  onDataChange(id, e) {
    const { name } = e.currentTarget
    let { value } = e.currentTarget

    const type = this.alertTypeToDataType(id)
    switch (type) {
      case 'bool':
        value = (value.toLowerCase() === 'true' || value === '1')
        break
      case 'float64':
        value = parseFloat(value)
        break
      default:
        break
    }
    this.onChange(id, name, { value })
  }

  onEditClick(id) {
    this.setState(
      (prevState) => ({
        isEditing: { ...prevState.isEditing, [id]: true },
      }),
    )
  }

  onSaveClick(id) {
    const { alertRules } = this.state
    if (id === 'new') {
      const rule = alertRules[id]
      rule.id = 0
      this.props.onAdd(rule)

      // Reset to default new rule values
      alertRules[id] = AlertRulesListView.newRule
      this.setState({ alertRules })
    } else {
      this.setState(
        (prevState) => ({
          isEditing: { ...prevState.isEditing, [id]: false },
        }),
      )
      this.props.onSave(alertRules[id])
    }
  }

  // Not sure where to put this
  alertTypeToDataType(id) {
    const rule = this.state.alertRules[id]
    switch (rule.type) {
      case 'leak_detected':
      case 'smoke_detected':
      case 'co_detected':
      case 'flow_detected':
        return 'bool'
      case 'offline':
        return 'string'
      case 'temp_low':
      case 'temp_high':
      case 'battery_low':
      default:
        return 'float64'
    }
  }

  actionButton(id) {
    const { isEditing } = this.state
    let actionButtonHtml
    if (isEditing[id]) {
      actionButtonHtml = (
        <IconButton aria-label="Save" onClick={() => this.onSaveClick(id)}>
          <SvgIcon>
            <path d={mdiContentSave} />
          </SvgIcon>
        </IconButton>
      )
    } else {
      actionButtonHtml = (
        <IconButton aria-label="Edit" onClick={() => this.onEditClick(id)}>
          <SvgIcon>
            <path d={mdiPencil} />
          </SvgIcon>
        </IconButton>
      )
    }
    return actionButtonHtml
  }

  renderAlertRule = (rule) => {
    const { classes, timeseries } = this.props
    const isEditing = this.state.isEditing[rule.id] || false

    return (
      <TableRow key={rule.id}>
        <TableCell>{rule.id}</TableCell>
        <TableCell>
          <TimeseriesSelect
            autoWidth
            disabled={!isEditing}
            timeseries={timeseries}
            value={rule.timeseries_id}
            onChange={(val) => this.onChange(rule.id, 'timeseries_id', val)}
          />
        </TableCell>
        <TableCell>
          <AlertTypeSelectContainer
            rule={rule}
            disabled={!isEditing}
            onChange={(e) => this.onChange(rule.id, 'type', e.target.value)}
          />
        </TableCell>
        <TableCell>
          <ComparatorSelect
            autoWidth
            disabled={!isEditing}
            value={rule.comparator}
            onChange={(val) => this.onChange(rule.id, 'comparator', val)}
          />
        </TableCell>
        <TableCell size="small">
          <Input
            className={classes.input}
            disabled={!isEditing}
            name="data"
            value={rule.data.value.toString()}
            onChange={(e) => this.onDataChange(rule.id, e)}
          />
        </TableCell>
        <TableCell>
          <Checkbox
            disabled={!isEditing}
            name="enabled"
            checked={rule.enabled}
            onChange={(e) => this.onCheckboxChange(rule.id, e)}
          />
        </TableCell>
        <TableCell>
          <Checkbox
            disabled={!isEditing}
            name="internal"
            checked={rule.internal}
            onChange={(e) => this.onCheckboxChange(rule.id, e)}
          />
        </TableCell>
        <TableCell padding="none">
          {this.actionButton(rule.id)}
        </TableCell>
      </TableRow>
    )
  }

  render() {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Timeseries</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Comparator</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell>Internal</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.values(this.state.alertRules).map(this.renderAlertRule)}
        </TableBody>
      </Table>
    )
  }
}

export default withStyles(AlertRulesListView.styles)(AlertRulesListView)
